import { useState } from "react";
import { Trans } from "react-i18next";
import { t } from "i18next";
import classNames from "classnames";
import { Input, Popover, Tooltip } from "antd";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconLineRefresh, IconLineDrag, IconLineCirclePlus } from "@kalodata/kds-design-icons";
import { Badge } from "@kalodata/kds-design";
import { IconCredit, IconLoading, IconDelete, IconHelp } from "@icons/index";
import { CommonButton } from "@components/CommonButton";
import {
  MAX_SELLING_POINTS,
  SELLING_POINT_MAX_LENGTH,
  SELLING_POINTS_AI_CREDITS,
  type SellingPointItem,
} from "./types";
import type { SellingPointsStatus } from "./useProductForm";

interface SortableItemProps {
  item: SellingPointItem;
  isFirst: boolean;
  onRemove: () => void;
  onContentChange: (content: string) => void;
}

const SortableItem = ({ item, isFirst, onRemove, onContentChange }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.content);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed.length <= SELLING_POINT_MAX_LENGTH) {
      onContentChange(trimmed);
    } else {
      setDraft(item.content);
    }
    setEditing(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform ? { ...transform, x: 0 } : null),
        transition,
        opacity: isDragging ? 0.5 : undefined,
      }}
      className={classNames("relative flex items-center gap-[8px] px-[12px] py-[10px] bg-kds-bg-card group")}
    >
      {!isFirst && <div className="absolute top-0 left-[12px] right-[12px] h-px bg-kds-border" />}
      {editing ? (
        <Input
          autoFocus
          value={draft}
          maxLength={SELLING_POINT_MAX_LENGTH}
          variant="borderless"
          className="flex-1 text-[14px] text-kds-fg px-0!"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onPressEnter={commit}
        />
      ) : (
        <span
          className="flex-1 text-[14px] text-kds-fg leading-[20px] cursor-text break-words"
          onClick={() => setEditing(true)}
        >
          {item.content}
        </span>
      )}
      <button
        type="button"
        className="opacity-0 group-hover:opacity-100 transition-opacity text-kds-fg-soft hover:text-kds-danger-fg bg-transparent border-none cursor-pointer p-0 flex items-center"
        onClick={onRemove}
        aria-label={t("Delete")}
      >
        <IconDelete className="w-[16px] h-[16px]" />
      </button>
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-kds-fg-soft bg-transparent border-none cursor-move p-0 flex items-center"
        aria-label={t("Drag")}
      >
        <IconLineDrag size={16} />
      </button>
    </div>
  );
};

interface AddSellingPointPopoverProps {
  onAdd: (content: string) => void;
  disabled: boolean;
}

const AddSellingPointPopover = ({ onAdd, disabled }: AddSellingPointPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed.length > SELLING_POINT_MAX_LENGTH) return;
    onAdd(trimmed);
    setValue("");
    setOpen(false);
  };

  const trigger = (
    <div
      className={classNames(
        "flex items-center justify-center gap-[6px] h-[44px] cursor-pointer border-t border-kds-border kalo-text-linear-blue",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <IconLineCirclePlus size={16} />
      <Trans>Add Selling Point</Trans>
    </div>
  );

  if (disabled) {
    return (
      <Tooltip title={t("You've reached the limit of {{max}} selling points.", { max: MAX_SELLING_POINTS })}>
        {trigger}
      </Tooltip>
    );
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottom"
      destroyOnHidden
      content={
        <div className="w-[360px]">
          <div className="border-b border-kds-border p-[12px]">
            <Input
              autoFocus
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={SELLING_POINT_MAX_LENGTH}
              onPressEnter={submit}
              placeholder={t("Enter your text in any language. We'll auto-translate it automatically.")}
            />
            {value.length >= SELLING_POINT_MAX_LENGTH && (
              <div className="mt-[4px] text-[12px] text-kds-danger-fg">
                <Trans>String must contain at most {{ max: SELLING_POINT_MAX_LENGTH }} character(s)</Trans>
              </div>
            )}
          </div>
          <div className="p-[12px] flex justify-end">
            <CommonButton type="primary" size="small" disabled={!value.trim()} onClick={submit}>
              <Trans>Save</Trans>
            </CommonButton>
          </div>
        </div>
      }
    >
      {trigger}
    </Popover>
  );
};

export interface SellingPointsSectionProps {
  items: SellingPointItem[];
  status: SellingPointsStatus;
  onChange: (items: SellingPointItem[]) => void;
  onAdd: (content: string) => void;
  onRemove: (id: string) => void;
  onRefresh?: () => void;
  refreshDisabled?: boolean;
  refreshDisabledTip?: string;
}

/**
 * Selling Points 整块区域（含 header、列表、Add Selling Point、Refresh）。
 *
 * 设计稿节点：3433:23455（链接模式）/ 3433:24599（手动模式带 Refresh + ✨20）
 */
export const SellingPointsSection = ({
  items,
  status,
  onChange,
  onAdd,
  onRemove,
  onRefresh,
  refreshDisabled = false,
  refreshDisabledTip,
}: SellingPointsSectionProps) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  };

  const updateContent = (id: string, content: string) => {
    onChange(items.map((sp) => (sp.id === id ? { ...sp, content } : sp)));
  };

  const isAtLimit = items.length >= MAX_SELLING_POINTS;
  const isLoading = status === "loading";

  const refreshButton = onRefresh && (
    <button
      type="button"
      disabled={refreshDisabled || isLoading}
      onClick={onRefresh}
      className={classNames(
        "inline-flex h-[32px] items-center gap-[6px] bg-transparent border-none cursor-pointer text-[14px] font-medium text-kds-accent-fg",
        (refreshDisabled || isLoading) && "opacity-50 cursor-not-allowed",
      )}
    >
      {isLoading ? (
        <IconLoading className="w-[20px] h-[20px] animate-spin" />
      ) : (
        <IconLineRefresh size={20} className="text-kds-accent-fg" />
      )}
      <Trans>Refresh Selling Points</Trans>
    </button>
  );

  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[4px] text-[14px] font-medium text-kds-fg">
          <Trans>Selling Points</Trans>
          <Tooltip title={t("Highlight key advantages of your product to improve generated videos.")}>
            <IconHelp className="w-[14px] h-[14px] text-kds-fg-soft cursor-help" />
          </Tooltip>
        </div>
        <div className="flex items-center gap-[12px]">
          {onRefresh &&
            (refreshDisabled && refreshDisabledTip ? (
              <Tooltip title={refreshDisabledTip}>{refreshButton}</Tooltip>
            ) : (
              refreshButton
            ))}
          {onRefresh && (
            <Badge
              rounded
              size="sm"
              type="neutral"
              variant="solid"
              className="px-[4px] gap-[2px] font-semibold bg-kds-bg-soft"
              iconLeft={<IconCredit width={16} height={16} />}
            >
              <span>{SELLING_POINTS_AI_CREDITS}</span>
            </Badge>
          )}
        </div>
      </div>
      <div className="bg-kds-bg-card border border-kds-border-subtle rounded-[8px] overflow-hidden">
        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center h-[120px] text-kds-fg-soft text-[14px] gap-[8px]">
            <IconLoading className="w-[16px] h-[16px] animate-spin" />
            <Trans>Generating selling points...</Trans>
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center h-[120px] text-kds-fg-subtle text-[14px]">
            <Trans>Add selling points to highlight your product.</Trans>
          </div>
        ) : (
          <div className="max-h-[240px] overflow-y-auto kds-scroll-thin">
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                {items.map((item, index) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    isFirst={index === 0}
                    onRemove={() => onRemove(item.id)}
                    onContentChange={(content) => updateContent(item.id, content)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
        <AddSellingPointPopover onAdd={onAdd} disabled={isAtLimit} />
      </div>
    </div>
  );
};
